/**
 * AuthModal — inline sign-in / sign-up sheet
 *
 * Shows as a centred modal overlay so the user never leaves the current page.
 * After successful auth it calls onSuccess() so the caller can continue the
 * action that triggered the modal (add to cart, buy now, etc.).
 */
import { useState } from "react";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Eye, EyeOff, X, Shield, Star, RefreshCw } from "lucide-react";
import axios from "axios";
import { API } from "../App";

const ERROR_MAP = {
  INVALID_CREDENTIALS: "Incorrect email or password.",
  USER_NOT_FOUND:      "No account found with this email.",
  ACCOUNT_DISABLED:    "Your account has been disabled.",
  TOO_MANY_REQUESTS:   "Too many attempts. Please wait and try again.",
};

function friendlyError(detail) {
  if (!detail) return "Authentication failed. Please try again.";
  return ERROR_MAP[detail] || detail;
}

export default function AuthModal({ onSuccess, onClose, defaultMode = "signin", onSaveCheckoutIntent }) {
  const { login, register } = useAuth();

  const [mode, setMode]         = useState(defaultMode); // "signin" | "signup"
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [name, setName]         = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let userData;
      if (mode === "signin") {
        userData = await login(email, password);
        toast.success("Welcome back!");
      } else {
        if (!name.trim()) { toast.error("Please enter your name"); setLoading(false); return; }
        userData = await register(email, password, name.trim());
        toast.success("Account created!");
      }
      onSuccess?.(userData);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(friendlyError(detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = () => {
    // Google OAuth requires a full page redirect.
    // 1. If caller provided a checkout intent saver (checkout page), use it.
    // 2. Always save the current URL so AuthCallback can return here after auth.
    if (typeof onSaveCheckoutIntent === "function") {
      onSaveCheckoutIntent();
    } else {
      // Save current path so we return here after Google auth (non-checkout pages)
      localStorage.setItem("rivora_google_return_path", window.location.pathname);
    }

    let redirectUrl;
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      redirectUrl = "http://localhost:3000/";
    } else {
      redirectUrl = window.location.origin + "/";
    }
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="relative w-full max-w-md bg-[#FAFAFA] shadow-2xl overflow-hidden">

        {/* Gold top accent */}
        <div className="h-1 w-full bg-[#D4AF37]" />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-900 transition-colors z-10"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="px-8 py-8">
          {/* Heading */}
          <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Rivora Jewels</p>
            <h2 className="cormorant text-3xl font-light text-zinc-900">
              {mode === "signin" ? "Sign In to Continue" : "Create Account"}
            </h2>
            <p className="text-sm text-zinc-500 mt-1">
              {mode === "signin"
                ? "Sign in to add items to your cart and checkout."
                : "Create a free account to start shopping."}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Name — signup only */}
            {mode === "signup" && (
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-zinc-300 bg-white px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 transition-colors"
                  placeholder="Your name"
                  required
                  autoFocus
                />
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-zinc-300 bg-white px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 transition-colors"
                placeholder="your@email.com"
                required
                autoFocus={mode === "signin"}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-zinc-300 bg-white px-4 py-3 pr-12 text-sm focus:outline-none focus:border-zinc-900 transition-colors"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 p-1"
                  aria-label={showPass ? "Hide password" : "Show password"}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800 py-3.5 uppercase tracking-widest text-xs transition-colors disabled:opacity-50"
            >
              {loading
                ? mode === "signin" ? "Signing in…" : "Creating account…"
                : mode === "signin" ? "Sign In" : "Create Account"}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-[#FAFAFA] px-3 text-zinc-400 tracking-widest">Or</span>
            </div>
          </div>

          {/* Google */}
          <button
            onClick={handleGoogleAuth}
            className="w-full border border-zinc-300 bg-white hover:bg-zinc-50 py-3 text-sm transition-colors flex items-center justify-center gap-2.5 mb-5"
          >
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>

          {/* Toggle mode */}
          <p className="text-center text-sm text-zinc-500">
            {mode === "signin" ? (
              <>
                Don't have an account?{" "}
                <button
                  onClick={() => setMode("signup")}
                  className="text-zinc-900 font-medium hover:underline underline-offset-2"
                >
                  Create one
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  onClick={() => setMode("signin")}
                  className="text-zinc-900 font-medium hover:underline underline-offset-2"
                >
                  Sign in
                </button>
              </>
            )}
          </p>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-6 pt-5 border-t border-zinc-100">
            {[
              { Icon: Shield,    label: "92.5 Certified" },
              { Icon: Star,      label: "4.9 Rating" },
              { Icon: RefreshCw, label: "Easy Returns" },
            ].map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-1.5 text-zinc-400">
                <Icon size={12} className="text-[#D4AF37]" />
                <span className="text-[10px] uppercase tracking-wide">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
